import React, { useContext, useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import { DocumentTypeSelectableRow } from "../../../shared/components/DocumentTypeSelectableRow";
import MainButton from "../../../shared/components/MainButton";
import Text from "../../../shared/components/Text";
import IconTitle from "../../../shared/components/IconTitle";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { useActionCallback } from "../../../../utils/hooks/useActionCallback";
import { ID_DOCUMENT_VERIFICATION_STATUSES } from "../config";

const DOCUMENT_TYPE_OPTIONS = [
  { value: "nif", label: "DNI_ES" },
  { value: "nie_eu", label: "NIE_UE" },
  { value: "nie_non_eu", label: "NIE_NON_UE" },
];

export const DocumentTypeSelection = ({ onSubmitForm }) => {
  const { palette, customBorderRadius } = useTheme();
  const {
    state: { participant, idDocumentVerification },
    dispatch,
  } = useContext(ProcessContext);
  const onboardingDetailsId = participant.onboardingDetails?.id;

  const [documentType, setDocumentType] = useState(() => {
    return (
      idDocumentVerification.documentType ?? DOCUMENT_TYPE_OPTIONS[0].value
    );
  });

  const [requestedStart, setRequestedStart] = useState(false);

  useEffect(() => {
    if (!idDocumentVerification) return;

    if (!idDocumentVerification.statusFetched) return;
    if (idDocumentVerification.loading) return;

    if (
      idDocumentVerification.status ===
      ID_DOCUMENT_VERIFICATION_STATUSES.COMPLETED
    ) {
      dispatch({ type: "SET_FLOW_ITEM_OK" });
    }
  }, [
    dispatch,
    idDocumentVerification.statusFetched,
    idDocumentVerification.loading,
    idDocumentVerification.status,
  ]);

  useEffect(() => {
    if (!requestedStart) return;
    if (idDocumentVerification.loading) return;
    if (idDocumentVerification.error) return;
    onSubmitForm({ document_type: documentType });
  }, [requestedStart, idDocumentVerification, onSubmitForm, documentType]);

  useActionCallback(
    "REQUEST_POST_ID_DOCUMENT_VERIFICATION_START_REJECTED",
    () => {
      setRequestedStart(false);
    }
  );

  if (!idDocumentVerification.statusFetched) {
    return (
      <>
        <Box />
        <Box
          display={"flex"}
          justifyContent={"center"}
          alignItems={"center"}
          width={"100%"}
          height={"100%"}
          gridGap={16}
        >
          <CircularProgress size={20} />
          <Text tId={"LOADING_VERIFICATION_STATUS"} />
        </Box>
        <Box />
      </>
    );
  }

  return (
    <>
      <Box className="appear-anim">
        <IconTitle
          title={
            <Text
              tId="DOCUMENT_ID_VERIFICATION"
              dynamicDataTexts={["DNI/NIE"]}
            />
          }
          subtitle={<Text tId="SELECT_YOUR_DOCUMENT_TYPE" />}
        />
        <Box style={{ height: "3vh" }} className="fade-in" />
        <Box mb={4}>
          <Typography variant="body1" color="textSecondary">
            <Text tId="DOCUMENT_TYPE_SELECTION_INSTRUCTIONS" />
          </Typography>
        </Box>
        <Box
          px={2}
          py={1}
          bgcolor={palette.grey[100]}
          borderRadius={customBorderRadius.paper}
          textAlign="left"
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {DOCUMENT_TYPE_OPTIONS.map((opt) => (
            <DocumentTypeSelectableRow
              key={opt.value}
              label={<Text tId={opt.label} />}
              selected={documentType === opt.value}
              onSelect={() => setDocumentType(opt.value)}
            />
          ))}
        </Box>
      </Box>
      <Box></Box>
      <Box>
        <Box
          mb={1}
          display="flex"
          gridGap={8}
          alignItems="center"
          justifyContent="flex-start"
        >
          <i
            className="material-icons"
            style={{
              fontSize: "18px",
              color: palette.primary.main,
              lineHeight: "18px",
            }}
          >
            {"lock"}
          </i>
          <Typography variant="caption" color="textSecondary">
            <Text tId="TRUSTED_ID_VERIFICATION" />
          </Typography>
        </Box>
        <Box
          mb={2}
          display="flex"
          gridGap={8}
          alignItems="center"
          justifyContent="flex-start"
        >
          <i
            className="material-icons"
            style={{
              fontSize: "18px",
              color: palette.primary.main,
              lineHeight: "18px",
            }}
          >
            {"18_up_rating"}
          </i>
          <Typography variant="caption" color="textSecondary">
            <Text tId="PROCESS_INFO_BULLET_NOT_UNDERAGED" />
          </Typography>
        </Box>
        <MainButton
          disabled={!onboardingDetailsId || idDocumentVerification?.loading}
          onClick={() => {
            dispatch({ type: "CLEAR_ID_DOCUMENT_VERIFICATION_ERROR" });
            setRequestedStart(true);
            dispatch({
              type: "SET_ID_DOCUMENT_VERIFICATION_DOCUMENT_TYPE",
              payload: documentType,
            });
            dispatch({
              type: "REQUEST_POST_ID_DOCUMENT_VERIFICATION_START",
              payload: {
                onboarding_details_id: onboardingDetailsId,
                document_type: documentType,
              },
            });
          }}
        >
          <Box
            display="flex"
            flexDirection="row"
            alignItems="center"
            gridGap={8}
          >
            <Text tId="START_VERIFICATION" />
            {idDocumentVerification?.loading && (
              <CircularProgress size={20} color="inherit" />
            )}
          </Box>
        </MainButton>
      </Box>
    </>
  );
};
